import type { EditorField } from "@/components/common/EditableGrid";
import { ResourceName, type DateRange } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { getProps } from "@/utils/helpers/columnsHelper";
import {
  overviewFields,
  overviewFieldsWithDependentEnrollment,
  overviewFieldsWithEmployeeEnrollment,
} from "../ClaimantOverviewTab";

/**
 * Field partitions for the Details tab (#7660). All derived from the Overview
 * tab's definitions so the two can never drift: the grid in edit mode and the
 * facts in read mode are the same `EditorField[]`.
 */

const MEMBER_IDENTIFIERS_SECTION = "Member Identifiers";

/** The `readOnlyReason` the dependent enrollment variant puts on the subscriber's fields. */
export const SUBSCRIBER_RECORD_REASON = "Edit on the subscriber's record";

export const identityFields: EditorField[] = overviewFields.filter((f) => f.section !== MEMBER_IDENTIFIERS_SECTION);

export const memberIdentifierFields: EditorField[] = overviewFields.filter(
  (f) => f.section === MEMBER_IDENTIFIERS_SECTION
);

const overviewSources = new Set(overviewFields.map((f) => f.source));

/** Enrollment-only part of the employee variant (the Overview grid prepends identity). */
export const employeeEnrollmentFields: EditorField[] = overviewFieldsWithEmployeeEnrollment.filter(
  (f) => !overviewSources.has(f.source)
);

/** Enrollment-only part of the dependent variant. */
export const dependentEnrollmentFields: EditorField[] = overviewFieldsWithDependentEnrollment.filter(
  (f) => !overviewSources.has(f.source)
);

// ─── Values ─────────────────────────────────────────────────────────────────

export const getByPath = (obj: unknown, path: string): unknown =>
  path.split(".").reduce<unknown>((acc, k) => {
    if (acc === null || acc === undefined) return acc;
    return (acc as Record<string, unknown>)[k];
  }, obj);

/**
 * Whether a field has something recorded. `false` is "not set" — same reading
 * as the grid's `!!value`, so a flag someone never turned on does not open its
 * group. Unlike the grid, a recorded 0 counts (hours used, premiums paid).
 */
export const isSet = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "" || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/** Mirrors EditableGrid's `showWhen` evaluation: `"!path"` negates. */
export const isShown = (field: EditorField, record: unknown): boolean => {
  if (!field.showWhen) return true;
  const negated = field.showWhen.startsWith("!");
  const path = negated ? field.showWhen.slice(1) : field.showWhen;
  const value = getByPath(record, path);
  return negated ? !value : !!value;
};

export const fieldLabel = (field: EditorField): string =>
  field.label ?? getProps(field.source, ResourceName.CLAIMANTS).label;

const longDate = (value: string) => formatDate(value, DATE_FORMATS.DISPLAY_LONG);

const formatRange = (range: DateRange): string => {
  const start = range.start_date ? longDate(range.start_date) : "";
  const end = range.end_date ? longDate(range.end_date) : "";
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;
  return "—";
};

/**
 * Read-mode text for a field, formatted as the in-place editors display it.
 * Returns an array for `dateRangeArray` (one line per range); references are
 * rendered by the caller (they need a fetch) and are not handled here.
 */
export const formatFieldValue = (field: EditorField, value: unknown): string | string[] => {
  switch (field.type) {
    case "date":
      return longDate(String(value));
    case "dollar":
      return formatUSD(Number(value));
    case "boolean":
      return value ? "Yes" : "No";
    case "select":
      return field.choices?.find((c) => c.id === value)?.name ?? String(value);
    case "dateRangeArray":
      return (value as DateRange[]).map(formatRange);
    case "number":
      return Number(value).toLocaleString("en-US");
    default:
      return String(value);
  }
};

// ─── Groups ─────────────────────────────────────────────────────────────────

export interface FieldGroup {
  /** The `section` the fields share; undefined for ungrouped fields. */
  title?: string;
  fields: EditorField[];
  /** Any field in the group has a recorded value (after `showWhen`). */
  hasValue: boolean;
}

/** Groups fields by `section`, in first-seen order, with a per-group has-value flag. */
export const groupFields = (fields: EditorField[], record: unknown): FieldGroup[] => {
  const groups: FieldGroup[] = [];
  for (const field of fields) {
    let group = groups.find((g) => g.title === field.section);
    if (!group) {
      group = { title: field.section, fields: [], hasValue: false };
      groups.push(group);
    }
    group.fields.push(field);
    if (isShown(field, record) && isSet(getByPath(record, field.source))) group.hasValue = true;
  }
  return groups;
};
